import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Mic, Settings, Loader2, Keyboard, Send, Trash2 } from "lucide-react";
import { agentsApi, fileUrl } from "../lib/api";
import { useVoiceChat } from "../hooks/useVoiceChat";
import { ShieldAlert } from "lucide-react";

export default function VoiceUI() {
  const [entered, setEntered] = useState(() => localStorage.getItem("age_ok") === "1");
  const [activeId, setActiveId] = useState("");
  const [text, setText] = useState("");
  const [listening, setListening] = useState(false);
  const [mode, setMode] = useState("text"); // 'text' | 'voice'
  const [menuOpen, setMenuOpen] = useState(false);
  const recogRef = useRef(null);
  const listenFlag = useRef(false);
  const recognizingRef = useRef(false);
  const speakingRef = useRef(false);
  const busyRef = useRef(false);
  const lastReplyRef = useRef("");
  const utterRef = useRef("");       // merged final text of the current utterance
  const sendTimerRef = useRef(null); // debounce: send once speech settles
  const scrollRef = useRef(null);

  const { data: agents = [] } = useQuery({ queryKey: ["agents"], queryFn: agentsApi.list });
  const { thinking, speaking, spokenWords, messages, sendMessage, clear } = useVoiceChat();

  useEffect(() => {
    if (!activeId && agents.length) setActiveId(agents[0].id);
  }, [agents, activeId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking]);

  const active = useMemo(() => agents.find((a) => a.id === activeId), [agents, activeId]);

  const confirmAge = () => {
    localStorage.setItem("age_ok", "1");
    setEntered(true);
  };

  const lastSentRef = useRef({ text: "", at: 0 });
  const doSend = (t) => {
    const val = (t ?? text).trim();
    if (!val || !activeId) return false;
    const now = Date.now();
    if (val === lastSentRef.current.text && now - lastSentRef.current.at < 4000) return false;
    lastSentRef.current = { text: val, at: now };
    sendMessage(val, activeId);
    setText("");
    return true;
  };

  // If a recognized phrase mostly matches Luka's last reply, it's the mic hearing her — drop it.
  const isEcho = (txt) => {
    const reply = lastReplyRef.current || "";
    if (!reply) return false;
    const words = txt.toLowerCase().split(/\s+/).filter((w) => w.length > 2);
    if (words.length === 0) return true;
    const hits = words.filter((w) => reply.includes(w)).length;
    return hits / words.length >= 0.55;
  };

  // Merge a new final chunk into the utterance, deduping Android's repeated finals.
  const mergeFinal = (prev, txt) => {
    if (!prev) return txt;
    const p = prev.toLowerCase();
    const t = txt.toLowerCase();
    if (t.includes(p)) return txt;      // Android grew the same final
    if (p.includes(t)) return prev;     // duplicate or shorter re-emit
    if (p.endsWith(t)) return prev;     // duplicate tail
    return (prev + " " + txt).trim();   // genuinely new words
  };

  const clearSendTimer = () => {
    if (sendTimerRef.current) { clearTimeout(sendTimerRef.current); sendTimerRef.current = null; }
  };

  const flushUtterance = () => {
    clearSendTimer();
    const toSend = utterRef.current.trim();
    utterRef.current = "";
    if (toSend && !busyRef.current && !isEcho(toSend)) doSend(toSend);
  };

  const startListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert("Голосовой ввод не поддерживается в этом браузере. Используйте текст.");
      setMode("text");
      return;
    }
    listenFlag.current = true;
    setListening(true);
    utterRef.current = "";
    const rec = new SR();
    rec.lang = "ru-RU";
    rec.continuous = true;
    rec.interimResults = true;
    rec.onstart = () => { recognizingRef.current = true; };
    rec.onresult = (e) => {
      // Mic is only active while Luka is idle; ignore anything captured while she's busy.
      if (busyRef.current) return;
      let gotFinal = false;
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (!r.isFinal) continue;
        const txt = (r[0]?.transcript || "").trim();
        if (!txt || isEcho(txt)) continue;
        utterRef.current = mergeFinal(utterRef.current, txt);
        gotFinal = true;
      }
      // Debounce: send the merged utterance ONCE after speech settles (no repeat bubbles).
      if (gotFinal) {
        clearSendTimer();
        sendTimerRef.current = setTimeout(flushUtterance, 1200);
      }
    };
    rec.onend = () => {
      recognizingRef.current = false;
      if (listenFlag.current && !busyRef.current) {
        try { rec.start(); } catch (e) {}
      } else if (!listenFlag.current) {
        setListening(false);
      }
    };
    rec.onerror = () => { recognizingRef.current = false; };
    recogRef.current = rec;
    try { rec.start(); } catch (e) {}
  };

  const stopListening = () => {
    listenFlag.current = false;
    setListening(false);
    clearSendTimer();
    utterRef.current = "";
    if (recogRef.current) { try { recogRef.current.stop(); } catch (e) {} }
  };

  const toggleMode = () => {
    setMenuOpen(false);
    if (mode === "text") { setMode("voice"); startListening(); }
    else { setMode("text"); stopListening(); }
  };

  useEffect(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") {
        lastReplyRef.current = (messages[i].text || "").toLowerCase();
        break;
      }
    }
  }, [messages]);

  useEffect(() => { speakingRef.current = speaking; }, [speaking]);
  useEffect(() => { busyRef.current = speaking || thinking; }, [speaking, thinking]);
  useEffect(() => {
    if (!listening) return;
    const busy = speaking || thinking;
    if (busy) {
      clearSendTimer();
      utterRef.current = "";
      if (recognizingRef.current && recogRef.current) {
        try { recogRef.current.abort(); } catch (e) {}
      }
    } else {
      const t = setTimeout(() => {
        if (listenFlag.current && !busyRef.current && !recognizingRef.current && recogRef.current) {
          try { recogRef.current.start(); } catch (e) {}
        }
      }, 900);
      return () => clearTimeout(t);
    }
  }, [speaking, thinking, listening]);

  const video = fileUrl(active?.circle_video_url);
  const avatar = fileUrl(active?.avatar_url);
  const name = (active?.name || "LUKA").toUpperCase();

  return (
    <div className="relative h-[100dvh] overflow-hidden bg-black text-white font-helv-thin" data-testid="voice-ui">
      {/* Fullscreen background video — whole frame, no cropping */}
      <div data-testid="agent-video" className="absolute inset-0 z-0 bg-black">
        {video ? (
          <video className="w-full h-full object-contain" src={video} autoPlay loop muted playsInline />
        ) : avatar ? (
          <img src={avatar} alt={name} className="w-full h-full object-contain" />
        ) : (
          <div className="w-full h-full bg-[#15151b]" />
        )}
      </div>

      {/* Narrow header — floats over the video, translucent with light blur */}
      <header className="absolute top-0 inset-x-0 z-30 flex items-center justify-between px-4 py-2.5 backdrop-blur-md backdrop-saturate-150">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full overflow-hidden border border-white/25 bg-[#1a1a20] flex items-center justify-center">
            {avatar ? (
              <img src={avatar} alt={name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-sm">{name[0]}</span>
            )}
          </div>
          <span data-testid="active-agent-name" className="text-sm tracking-[0.35em] font-helv-thin">
            {name}
          </span>
        </div>
        <button
          data-testid="burger-menu-btn"
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="menu"
          className="flex flex-col justify-center gap-[6px] p-2"
        >
          <span className="block w-6 h-px bg-white/80" />
          <span className="block w-6 h-px bg-white/80" />
        </button>

        {menuOpen && (
          <div
            data-testid="burger-menu"
            className="absolute right-3 top-full mt-1 w-60 bg-[#141418] border border-white/10 rounded-xl shadow-2xl z-40 p-2 font-helv-thin"
          >
            <p className="px-2 py-1 text-[11px] tracking-[0.2em] uppercase text-white/35">Agent</p>
            {agents.map((a) => (
              <button
                key={a.id}
                data-testid={`menu-agent-${a.id}`}
                onClick={() => { setActiveId(a.id); clear(); setMenuOpen(false); }}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-white/5 ${a.id === activeId ? "text-white" : "text-white/60"}`}
              >
                {a.name}
              </button>
            ))}
            <div className="my-1 h-px bg-white/10" />
            <button
              data-testid="menu-clear"
              onClick={() => { clear(); setMenuOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-white/5"
            >
              <Trash2 className="w-4 h-4" /> Очистить чат
            </button>
            <Link
              to="/admin"
              data-testid="menu-admin"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/70 hover:bg-white/5"
            >
              <Settings className="w-4 h-4" /> Админ-панель
            </Link>
          </div>
        )}
      </header>

      {/* 18+ confirmation plaque — floats near the top, video shows below (same blur as header) */}
      {!entered && (
        <div
          data-testid="18-plus-gate-modal"
          className="absolute top-16 inset-x-0 z-40 px-4 flex justify-center animate-fadeup"
        >
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0b0b0d]/35 backdrop-blur-md backdrop-saturate-150 shadow-[0_8px_32px_rgba(0,0,0,0.5)] p-6 font-helv-thin">
            <div className="flex items-center gap-2 text-[#8fd0ff] mb-3">
              <ShieldAlert className="w-5 h-5" />
              <span className="text-[11px] tracking-[0.3em] uppercase font-helv-thin">18+ только для взрослых</span>
            </div>
            <h2 className="text-lg font-helv-thin text-white mb-2">Вам есть 18 лет?</h2>
            <p className="text-sm text-white/60 leading-relaxed mb-5 font-helv-thin">
              Здесь живёт ИИ-персонаж для взрослой аудитории. Входя, вы подтверждаете, что вам не меньше 18 лет.
            </p>
            <div className="flex flex-col gap-2.5">
              <button
                data-testid="age-confirm-button"
                onClick={confirmAge}
                className="w-full h-11 rounded-full bg-[#2f6bd4] hover:bg-[#2f86e0] text-white text-sm tracking-wide transition-colors font-helv-thin"
              >
                Мне есть 18 — войти
              </button>
              <a
                href="https://www.google.com"
                data-testid="age-exit-link"
                className="w-full h-11 flex items-center justify-center rounded-full border border-white/20 text-white/70 hover:text-white hover:border-white/40 text-sm transition-colors font-helv-thin"
              >
                Выйти
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Bottom floating panel — transcript + input over the video, translucent dark + blur */}
      {entered && (
      <div className="absolute bottom-0 inset-x-0 z-20 flex flex-col bg-gradient-to-t from-black/85 via-black/45 to-transparent pt-16">
        {/* Transcript window (duplicates the voice) */}
        <div
          ref={scrollRef}
          data-testid="conversation"
          className="max-h-[34dvh] overflow-y-auto px-4 py-3 space-y-2 font-helv-thin flex flex-col"
        >
          {messages.map((m, i) => {
            if (m.role === "system") {
              return (
                <p key={i} data-testid="system-message" className="text-center text-white/50 text-sm italic py-1">
                  {m.text}
                </p>
              );
            }
            const isUser = m.role === "user";
            const isActiveAssistant = !isUser && i === messages.length - 1;
            return (
              <div
                key={i}
                data-testid={isUser ? "user-message" : "ai-message"}
                className={`max-w-[85%] px-4 py-2.5 rounded-2xl leading-relaxed animate-subin font-helv-thin backdrop-blur-md backdrop-saturate-150 border border-white/10 shadow-lg ${
                  isUser
                    ? "self-end ml-auto bg-[#2f6bd4]/35 text-white rounded-br-sm"
                    : "self-start mr-auto bg-[#0b0b0d]/35 text-[#8fd0ff] rounded-bl-sm"
                }`}
              >
                {isActiveAssistant ? (
                  m.text.split(/(\s+)/).map((tok, wi, arr) => {
                    if (/^\s+$/.test(tok)) return tok;
                    const wordIdx = arr.slice(0, wi).filter((t) => !/^\s+$/.test(t)).length;
                    const spoken = wordIdx < spokenWords;
                    const current = wordIdx === spokenWords && speaking;
                    return (
                      <span
                        key={wi}
                        className={`transition-colors duration-200 ${
                          current ? "text-white" : spoken ? "text-[#8fd0ff]" : "text-[#8fd0ff]/35"
                        }`}
                      >
                        {tok}
                      </span>
                    );
                  })
                ) : (
                  m.text
                )}
              </div>
            );
          })}
          {thinking && (
            <div data-testid="ai-typing" className="self-start mr-auto bg-[#0b0b0d]/35 backdrop-blur-md backdrop-saturate-150 border border-white/10 text-[#8fd0ff] px-4 py-3 rounded-2xl rounded-bl-sm flex items-center gap-1 w-fit">
              <span className="w-2 h-2 bg-[#8fd0ff] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-2 h-2 bg-[#8fd0ff] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="w-2 h-2 bg-[#8fd0ff] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          )}
        </div>

        {/* Center mode toggle + text input — darker translucent for readability */}
        <div className="px-4 pb-6 pt-3 border-t border-white/10 bg-[#0b0b0d]/60 backdrop-blur-xl backdrop-saturate-150 flex flex-col items-center gap-3">
          <button
            data-testid="mode-toggle-btn"
            onClick={toggleMode}
            className="breathe-blue flex items-center gap-2 px-12 py-3 rounded-full border text-sm tracking-[0.35em] uppercase font-helv-thin text-[#8fd0ff] hover:bg-white/5 transition-colors"
          >
            {mode === "text" ? (
              <><Mic className="w-4 h-4" /> voice</>
            ) : (
              <><Keyboard className="w-4 h-4" /> text</>
            )}
          </button>

          {mode === "text" && (
            <div className="w-full max-w-xl flex items-center gap-2 bg-[#141418]/70 backdrop-blur-md border border-[#3b9eff]/55 rounded-full px-4 py-2 font-helv-thin">
              <input
                data-testid="voice-text-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doSend()}
                placeholder={`Написать ${active?.name || "агенту"}…`}
                className="flex-1 bg-transparent outline-none text-white placeholder:text-white/35 font-helv-thin"
              />
              <button
                data-testid="voice-send-button"
                onClick={() => doSend()}
                className="flex items-center justify-center w-9 h-9 rounded-full bg-[#3b9eff] text-white hover:bg-[#2f86e0] transition-colors"
              >
                {thinking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
